import { ref, watch, onBeforeUnmount } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';

const DEFAULT_WIDTH = 340;
const MIN_WIDTH = 260;
const MAX_WIDTH = 600;

/**
 * Clamp a numeric value between MIN_WIDTH and MAX_WIDTH.
 * Returns null if value is not a finite number (guards against NaN/Infinity
 * from corrupted uiSettings or non-numeric localStorage data).
 */
function clampWidth(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, num));
}

export function useConversationListResize() {
  const { uiSettings, updateUISettings } = useUISettings();

  // Initialize from saved settings.
  // Returns null if no valid saved width exists — this signals ChatList
  // to use its default Tailwind classes (including responsive 2xl breakpoint).
  const savedRaw = uiSettings.value?.conversation_list_width;
  const clamped = clampWidth(savedRaw);

  // null = user never resized → ChatList uses Tailwind defaults
  const listWidth = ref(clamped);
  const isDragging = ref(false);

  let startX = 0;
  let startWidth = 0;

  function startDrag(event) {
    // Guard against double-start stacking listeners
    if (isDragging.value) return;

    event.preventDefault();
    isDragging.value = true;
    startX = event.clientX;
    // If listWidth is null (never resized), read from DOM for accurate start
    startWidth = listWidth.value ?? DEFAULT_WIDTH;

    document.addEventListener('mousemove', onDrag);
    document.addEventListener('mouseup', stopDrag);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }

  function onDrag(event) {
    if (!isDragging.value) return;
    const delta = event.clientX - startX;
    const newWidth = clampWidth(startWidth + delta);
    if (newWidth !== null) {
      listWidth.value = newWidth;
    }
  }

  function stopDrag() {
    if (!isDragging.value) return;
    isDragging.value = false;
    document.removeEventListener('mousemove', onDrag);
    document.removeEventListener('mouseup', stopDrag);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    // Persist to UI settings
    if (listWidth.value !== null) {
      updateUISettings({ conversation_list_width: listWidth.value });
    }
  }

  function resetWidth() {
    listWidth.value = null;
    updateUISettings({ conversation_list_width: null });
  }

  // Sync if settings change externally (e.g., another tab)
  watch(
    () => uiSettings.value?.conversation_list_width,
    newVal => {
      if (isDragging.value) return;
      if (newVal == null) {
        listWidth.value = null;
        return;
      }
      const val = clampWidth(newVal);
      if (val !== null) {
        listWidth.value = val;
      }
    }
  );

  onBeforeUnmount(() => {
    document.removeEventListener('mousemove', onDrag);
    document.removeEventListener('mouseup', stopDrag);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  });

  return {
    listWidth,
    isDragging,
    startDrag,
    resetWidth,
    DEFAULT_WIDTH,
    MIN_WIDTH,
    MAX_WIDTH,
  };
}
